import React, { type JSX } from 'react';

import {
    widgetList,
    setDashboardId,
    resetDashboard,
    configureSummaryValues,
    mqttTopics,
    removeSummaryWidgetLoader,
    setWidgetList,
    setSummaryResponse,
} from '@fiji/common/src/features/dashboardManagement/commonDashboardSlice';
import { CustomTransComponent, DynamicWidget, FailedLoadTriggerModal, Loader } from '../../components';
import { useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';

import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import RGL, { WidthProvider } from 'react-grid-layout';
import { Stack, Box, Button, Card, CardContent, CardHeader, Typography, Divider } from '@mui/material';
import { resetLocalDashboard } from '@fiji/common/src/features/dashboardManagement/dashboardSlice';
import { CreateDashboardModal } from './CreateDashboard';
import Widgets from '@mui/icons-material/Widgets';
import DesignServices from '@mui/icons-material/DesignServices';

import { useGetActiveDashboardsQuery } from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import { useGetChannelDetailsMutation } from '@fiji/common/src/features/deviceManagement/deviceApi';
import {
    useCommonWidgetConfiguration,
    useConfirm,
    useDashboardCommonHandlers,
    useGetWidgetDetails,
} from '@fiji/common/src/hooks';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { AddVirtualModal } from 'pages/VirtualLoadDetails/AddVirtualModal';
import { DetailsCardSkeleton } from 'pages/WidgetManagement/common/Skeletons';
import { useNewCustomTranslations, useRBAC } from 'hooks';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useHandleVirtualLoads } from 'pages/VirtualLoadDetails/hooks';
import { GRID_COLS, GRID_ROWS } from '@fiji/common/src/constants';
import { subscribeTopic, unsubscribeTopic } from '@fiji/common/src/mqtt_connection/mqttConnection';
import { api } from '@fiji/common/src/app/api/baseApi';
import { ScoreModal } from 'pages/WidgetManagement/common/ScoreModal';
import { downloadFile, downloadFileFromLink, getUTCTimestamp } from 'utils/helpers';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { useTranslation } from 'react-i18next';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { SnoozeHeader } from './SnoozeHeader';
import { EsmCriticalHeader } from './EsmCriticalHeader';
import { resetTrendsLegendData } from '@fiji/common/src/features/widgetManagement/trendsWidgetSlice';
import {
    getWidgetTypeValidationConfig,
    isWidgetApplicable,
    isWidgetConfigured,
    customEndpointSelector,
    systemTimezone,
    timeStampHandler,
} from '@fiji/common/src/utils/helpers';
import { ErrorOutline } from '@mui/icons-material';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';
import { UserProfile } from '@fiji/common/src/types';
import { CHART_TYPE } from 'pages/WidgetManagement/common';
import { ENERGY_FLOW_WIDGET_POLLING_INTERVAL_IN_MS } from '@fiji/common/src/constants/cacheTimeout';
import { AddConfirmationModal } from 'pages/CommandBarWidget/AddConfirmationModal';
import { AddConfirmationModalWithInput } from 'pages/CommandBarWidget/AddConfirmationModalWithInput';
import { AddConfirmationForFaultsWarnings } from 'pages/CommandBarWidget/AddConfirmationForFaultsWarnings';
import { setFailedLoadTrigger } from '@fiji/common/src/features/loadGroups/loadGroupsSlice';
import { useUpdateLoadGroupsMutation } from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import type { GroupState } from 'components/CustomSwitchBtn/CustomSwitchBtn';
import {
    useLazyGetDashboardWidgetByIdQuery,
    useUpdateWidgetMutation,
} from '@fiji/common/src/features/widgetManagement/widgetApi';
import { SelectLoadGroupsModal } from 'pages/WidgetManagement/Widgets/load_groups/SelectLoadGroupsModal';
import { CreateLoadGroupModal } from 'pages/LoadsGroups/CreateLoadGroupModal';
import { computeMinRowsForWidget, decodeMqttGroupState, isDefaultDetailsWidget } from './Summary.utils';

const WIDGET_GRID_GAP = 16;
const GRID_ROW_MARGIN_Y = WIDGET_GRID_GAP;
const GRID_ITEM_GAP = WIDGET_GRID_GAP;
const GRID_LAYOUT_MARGIN: [number, number] = [GRID_ITEM_GAP, GRID_ROW_MARGIN_Y];

const ResponsiveReactGridLayout = WidthProvider(RGL);

export const computeAdjustedHeight = (widget: any, height: number, minRows: number): number => {
    // Only adjust height for non-detail widgets with single row
    const widgetTypeId = widget?.widgetType?.id;
    if (!isDefaultDetailsWidget(widgetTypeId) && minRows === 1) {
        return Math.max(0, height - 18);
    }
    return height;
};

export const getTrendsPayloadKey = (widgetType: any): string =>
    widgetType === 'load_group_trends' ? 'load_group_trends-getLoadGroupTrends' : 'trends';

export const getTrendsApiEndpoint = (widgetType: any): string =>
    widgetType === 'trends' ? 'getDeviceTrends' : 'getLoadGroupTrends';

export const findWidgetById = (widgets: any[] | undefined, widgetId: any): any =>
    widgets?.find((item: any) => item?.id === widgetId);

export const cloneConfig = (source: any): any => JSON.parse(JSON.stringify(source ?? {}));

export const buildTrendsExportBody = (
    payloadKey: string,
    selectedPayload: any,
    payload: any,
    selectedWidgetClone: any,
    selectedEntities?: any
): any => {
    if (payloadKey !== 'trends') {
        return {
            exportRequests: selectedPayload?.channelIds,
            from: selectedPayload?.activeDuration?.from,
            to: selectedPayload?.activeDuration?.to,
        };
    }
    const built = payload[payloadKey](selectedWidgetClone, selectedEntities);
    return {
        exportRequests: built['payload'],
        from: getUTCTimestamp(built['from']),
        to: getUTCTimestamp(built['to']),
    };
};

export const buildDurationTrendsPayload = (
    widgetType: any,
    payloadKey: string,
    trendsPayload: any,
    payload: any,
    selectedWidgetClone: any,
    selectedEntities?: any
): any => {
    const base =
        widgetType === 'load_group_trends'
            ? { payload: trendsPayload?.payload }
            : JSON.parse(JSON.stringify(payload[payloadKey](selectedWidgetClone, selectedEntities)));
    base['from'] = trendsPayload?.activeDuration?.from;
    base['to'] = trendsPayload?.activeDuration?.to;
    base['loadTrend'] = payloadKey !== 'trends';
    return base;
};

export const buildLegendsRequestPayload = (
    widgetType: any,
    payloadKey: string,
    selectionPayload: any,
    payload: any,
    selectedWidgetClone: any,
    selectedEntities?: any
): any => {
    const base =
        widgetType === 'load_group_trends'
            ? { payload: selectionPayload?.payload }
            : JSON.parse(JSON.stringify(payload[payloadKey](selectedWidgetClone, selectedEntities)));
    base['from'] = selectionPayload?.activeDuration?.from;
    base['to'] = selectionPayload?.activeDuration?.to;
    base['loadTrend'] = payloadKey !== 'trends';
    return base;
};

export const buildTimeWindowedPayloadData = (
    data: any,
    from: any,
    to: any,
    prevFrom: any,
    prevTo: any,
    normalizedTimeFilter: any,
    widgetTypeId: any,
    unitType: any
): any => {
    if (widgetTypeId === 'metrics' || widgetTypeId === 'load_trends') {
        return {
            ...data,
            currentState: { startTime: from, endTime: to },
            previousState: { startTime: prevFrom, endTime: prevTo },
            timeFilter: normalizedTimeFilter,
        };
    }
    if (widgetTypeId === 'weather') {
        return { ...data, timeFilter: normalizedTimeFilter };
    }
    const base = { ...data, startTime: from, endTime: to, timeFilter: normalizedTimeFilter };
    if (widgetTypeId === 'aggregated_trends') return { ...base, unit: unitType };
    return base;
};

export const isPeriodTrackingWidget = (widgetTypeId: any): boolean =>
    widgetTypeId === 'load_trends' || widgetTypeId === 'weather' || widgetTypeId === 'aggregated_trends';

export const buildFilterUpdatedConfig = (widgetToUpdate: any, responseData: any, eventValue: any): any => ({
    ...widgetToUpdate.config,
    secondary: responseData,
    ...(isPeriodTrackingWidget(widgetToUpdate?.widgetType?.id) && { selectedTimePeriod: eventValue }),
});

export const applyLoadingToChannel = (loadingConfig: any, commandBarData: any): void => {
    if (loadingConfig?.secondary?.[0]?.channels) {
        loadingConfig.secondary.forEach((channelGroup: any) => {
            channelGroup?.channels?.forEach((ch: any) => {
                if (ch?.deviceId === commandBarData?.deviceId && ch?.channelId === commandBarData?.channelId) {
                    ch.isLoading = true;
                }
            });
        });
        return;
    }
    const idx = loadingConfig.secondary?.findIndex(
        (ch: any) => ch?.deviceId === commandBarData?.deviceId && ch?.channelId === commandBarData?.channelId
    );
    if (idx !== -1) loadingConfig.secondary[idx].isLoading = true;
};

export const stripLoadingFromChannel = (ch: any): any => {
    const chRest = { ...ch };
    delete chRest.isLoading;
    return chRest;
};

export const stripLoadingFromItem = (item: any): any => {
    const rest = { ...item };
    delete rest.isLoading;
    if (rest?.channels) {
        rest.channels = rest.channels.map(stripLoadingFromChannel);
    }
    return rest;
};

export const buildLoadingClearedFallback = (widget: any): any => {
    const fallback = cloneConfig(widget?.config);
    if (fallback?.secondary) {
        fallback.secondary = fallback.secondary.map(stripLoadingFromItem);
    }
    return fallback;
};

export const buildMapConfigFilters = (selectedWidgetClone: any, selectedNode: any, deviceId: any): any => {
    const configFilters = selectedWidgetClone?.config?.filters;
    if (configFilters && selectedWidgetClone?.config?.multiDevice) {
        return configFilters;
    }
    if (selectedNode?.wrapperDeviceType === 'Gateway') {
        return { ...configFilters, parentId: [deviceId] };
    }
    return { ...configFilters, deviceIds: [deviceId] };
};

export const mapDeviceMapItemsToSecondary = (mapData: any): any[] =>
    mapData?.map((item: any) => {
        const firstDevice = item?.deviceList?.[0];
        return {
            coordinates: item.coordinates,
            deviceCount: item.deviceCount,
            name: firstDevice?.name ?? '',
            id: firstDevice?.id,
            tileKey: item?.tileKey,
            deviceList: item?.deviceList,
        };
    });

export const isCommandBarWidgetForDeviceItem = (item: any, selectedDeviceId: string): boolean =>
    item?.widgetType?.id === 'command_bar' &&
    item?.config?.secondary?.some((channelData: any) => channelData?.deviceId === selectedDeviceId);

export const stripLoadingKeysFromWidget = (widgetClone: any): void => {
    widgetClone?.secondary?.forEach((channel: any) => {
        channel?.channels?.forEach((item: any) => {
            if (item?.isLoading) {
                delete item?.isLoading;
            }
        });
    });
};

export const buildUnpinnedLayoutItem = (item: any, layoutTemplate: any): any => ({
    ...layoutTemplate,
    i: item?.id,
    h: 6,
    minH: 2,
    minW: 14,
    moved: false,
    static: false,
    w: 49,
    x: 0,
    y: 0,
});

export const shouldPrependUnpinnedItems = (widgetListUnpinned: any[] | undefined, dashboardLayout: any): boolean =>
    Boolean(widgetListUnpinned?.length && dashboardLayout?.[0]);

export const buildBaseWidgetLayout = (widgets: any[] | undefined, dashboards: any): any[] | undefined => {
    const dashboardLayout = dashboards?.data?.widgetLayout;
    if (widgets?.length === dashboardLayout?.length) return dashboardLayout;

    const widgetListUnpinned = widgets?.filter(
        (item: any) => !item?.config?.isPinned && !dashboardLayout?.some((subItem: any) => subItem?.i === item?.id)
    );

    if (!shouldPrependUnpinnedItems(widgetListUnpinned, dashboardLayout)) return dashboardLayout;
    return [
        ...(widgetListUnpinned ?? []).map((item: any) => buildUnpinnedLayoutItem(item, dashboardLayout[0])),
        ...dashboardLayout,
    ];
};

export const applyLayoutMeasuredHeight = (
    layoutItem: any,
    measuredHeights: Record<string, number>,
    minRows: number
): any => {
    const measuredH = measuredHeights[layoutItem.i];
    if (!measuredH) return layoutItem;
    const newH = Math.max(minRows, measuredH);
    const nextMinH = Math.max(minRows, Math.min(layoutItem?.minH ?? newH, newH));
    if (newH === layoutItem.h && nextMinH === layoutItem?.minH) return layoutItem;
    return { ...layoutItem, h: newH, minH: nextMinH };
};

export const isDashboardAlignedWithWidgets = (widgets: any[] | undefined, dashboards: any): boolean => {
    if (widgets?.length !== dashboards?.data?.widgetIdList?.length) return false;
    return (
        widgets?.every?.((item: any) => dashboards?.data?.widgetIdList?.find((subItem: any) => subItem === item?.id)) ??
        false
    );
};

export const isEmptyDashboardsReady = (
    dashboards: any,
    isSuccess: boolean,
    isError: boolean,
    isLoading: boolean,
    isFetching: boolean,
    isConfigLoading: boolean
): boolean => !dashboards?.data && (isSuccess || isError) && !isLoading && !isFetching && !isConfigLoading;

export const resolveGatewayDeviceListType = (widget: any, wrapperDeviceType: any): 'GROUP' | 'DEVICE' => {
    const widgetTypeId = widget?.widgetType?.id;
    return wrapperDeviceType === 'Gateway' && widgetTypeId === 'device_list' ? 'GROUP' : 'DEVICE';
};

export const resolveWidgetTimeWindow = (widgetToUpdate: any, profileDetails: any, eventValue: any): any => {
    const timezone = profileDetails?.data?.timezone ?? systemTimezone;
    const calendarWeek = profileDetails?.data?.calendarWeek;
    if (widgetToUpdate?.widgetType?.id === 'load_trends') {
        return timeStampHandler(timezone, calendarWeek, eventValue, null, null, 'load_trends');
    }
    return timeStampHandler(timezone, calendarWeek, eventValue);
};

export const resolveWidgetEndpoint = (widgetTypeId: any, configType: any): any =>
    customEndpointSelector(widgetTypeId, configType)?.[0]?.['endpoint'];

export const resolveConfigTimeWindow = (config: any, profileDetails: any): { from: any; to: any } =>
    timeStampHandler(
        profileDetails?.data?.timezone ?? systemTimezone,
        profileDetails?.data?.calendarWeek,
        config?.selectedTimePeriod
    );

export const isCommandFallbackTooSoon = (lastFetchAt: number): boolean => Date.now() - lastFetchAt < 500;

export const dispatchWidgetPolling = (
    dispatch: any,
    payload: any,
    payloadKey: string,
    endpointName: string,
    selectedWidgetConfig: any,
    widget: any
): void => {
    const payloadFn = payload?.[payloadKey] || payload?.['default'];
    dispatch(
        (api as any)?.endpoints?.[endpointName]?.initiate(payloadFn?.(selectedWidgetConfig), {
            forceRefetch: true,
        })
    )
        .then((response: any) => {
            dispatch(setSummaryResponse({ widget, responses: [response] }));
        })
        .catch((error: any) => {
            console.error(error);
        });
};

type CommandBarModalRefs = {
    faults: any;
    withInput: any;
    single: any;
};

export const hasNullOrUndefinedBlockValue = (commandBarData: any): boolean =>
    commandBarData?.blockValue === null || commandBarData?.blockValue === undefined;

export const invokeCommandBarModal = (
    modalRef: any,
    commandBarData: any,
    widgetId: string,
    setWidgetID: (id: string) => void
): void => {
    setWidgetID(widgetId);
    modalRef?.current?.handleModalAction(true, commandBarData);
};

export const openCommandBarModal = (
    modalRefs: CommandBarModalRefs,
    commandBarData: any,
    handleCommandBarAction: (commandBarData: any, widgetId: string) => void,
    widgetId: string,
    setWidgetID: (id: string) => void
): void => {
    if (commandBarData?.length > 1) {
        invokeCommandBarModal(modalRefs.faults, commandBarData, widgetId, setWidgetID);
        return;
    }
    if (hasNullOrUndefinedBlockValue(commandBarData)) {
        invokeCommandBarModal(modalRefs.withInput, commandBarData, widgetId, setWidgetID);
        return;
    }
    if (commandBarData?.isConfirm) {
        invokeCommandBarModal(modalRefs.single, commandBarData, widgetId, setWidgetID);
        return;
    }
    handleCommandBarAction(commandBarData, widgetId);
};

export const handleCommandBarPress = (
    commandBarData: any,
    widgetId: string,
    canEditControls: boolean,
    modalRefs: CommandBarModalRefs,
    handleCommandBarAction: (commandBarData: any, widgetId: string) => void,
    setWidgetID: (id: string) => void,
    dispatch: any,
    t: any
): void => {
    if (!canEditControls) {
        dispatch(
            setMessageContent({
                isOpen: true,
                message: t('WIDGET_MANAGEMENT:ACCESS_DENIED'),
            })
        );
        return;
    }
    openCommandBarModal(modalRefs, commandBarData, handleCommandBarAction, widgetId, setWidgetID);
};

export const isFailedLoadTriggerMessage = (message: any): boolean =>
    message?.type === 'FAILED_LOAD_TRIGGER_FOR_LOAD_GROUP';

export const isLoadGroupStateChangeMessage = (message: any): boolean => message?.type === 'LOAD_GROUP_STATE_CHANGE';

export const isCommandOrRunTestMessage = (message: any): boolean =>
    message?.type === 'command' || message?.type === 'runTest';

export const dispatchCommandMessageAlert = (message: any, dispatch: any): void => {
    const alertMsg = message?.message;
    if (!alertMsg) return;
    dispatch(setMessageContent({ isOpen: true, message: alertMsg }));
};

export const buildLoadGroupsSelectedNodes = (selectedGroups: any[]): any[] =>
    selectedGroups.map((item: any) => ({
        id: item?.id,
        name: item?.name,
        deviceName: item?.deviceName,
        groupPath: item?.groupPath,
    }));

export const dispatchLoadGroupsListInitiate = (dispatch: any, loadGroupsPayload: any): Promise<any> =>
    dispatch((api as any)?.endpoints?.['getLoadGroupsList']?.initiate(loadGroupsPayload, { forceRefetch: true }));

const refetchLoadGroupsList = async (
    dispatch: any,
    payload: any,
    configClone: any,
    widgetId: string,
    widgetResponse: any
): Promise<void> => {
    const loadGroupsPayload = payload?.['load_groups']?.(configClone);
    if (!loadGroupsPayload) return;
    const response = await dispatchLoadGroupsListInitiate(dispatch, loadGroupsPayload);
    if (response?.data) {
        widgetResponse['load_groups']?.([response], widgetId);
    }
};

export const scheduleLoadGroupFallback = (
    loadGroupId: any,
    widgetId: string,
    toggleStartedAt: number,
    lastLoadGroupMqttAtRef: React.MutableRefObject<Record<string, number>>,
    refreshLoadGroupsWidget: (id: string) => Promise<void>,
    setLoadGroupStates: React.Dispatch<React.SetStateAction<Record<string, GroupState>>>
): ReturnType<typeof setTimeout> =>
    setTimeout(() => {
        const idStr = String(loadGroupId);
        const lastMqttAt = lastLoadGroupMqttAtRef.current[idStr] ?? 0;
        if (lastMqttAt >= toggleStartedAt) return;

        void refreshLoadGroupsWidget(widgetId).finally(() => {
            setLoadGroupStates((prev) => {
                if (prev[idStr] !== 'PENDING') return prev;
                const next = { ...prev };
                delete next[idStr];
                return next;
            });
        });
    }, 60000);

export const dispatchLegendsRefetch = (
    dispatch: any,
    endpoint: string,
    newPayload: any,
    selectedWidgetClone: any,
    selectionPayload: any,
    widgetResponse: any
): void => {
    dispatch((api as any)?.endpoints?.[endpoint]?.initiate(newPayload, { forceRefetch: true })).then(
        (response: any) => {
            widgetResponse[selectedWidgetClone?.widgetType?.id]([response], selectionPayload?.widgetId, {
                selectedWidget: selectedWidgetClone,
            });
        }
    );
};

export const updateTrendsConfigSecondary = (selectedWidgetClone: any, selectionPayload: any, dispatch: any): void => {
    selectedWidgetClone['config']['secondary'] = selectionPayload.payload;
    dispatch(configureSummaryValues({ widgetId: selectionPayload?.widgetId, config: selectedWidgetClone }));
};

export const dispatchTrendsRefetch = (
    dispatch: any,
    endpoint: string,
    newPayload: any,
    trendsPayload: any,
    widgetType: any,
    widgetResponse: any
): void => {
    dispatch((api as any)?.endpoints?.[endpoint]?.initiate(newPayload, { forceRefetch: true })).then(
        (response: any) => {
            widgetResponse[widgetType]([response], trendsPayload?.widgetId, {
                duration: (trendsPayload?.activeDuration?.to - trendsPayload?.activeDuration?.from) / 1000 / 60 / 60,
            });
        }
    );
};

export const buildExportTrendsRequestBody = (exportBody: any, selectedWidgetClone: any, payloadKey: string): any => ({
    body: {
        ...exportBody,
        types: ['DOWNLOAD'],
        chartType: CHART_TYPE[selectedWidgetClone?.type ?? 'line'],
        loadTrend: payloadKey !== 'trends',
    },
    params: { type: 'CSV' },
});

export const handleExportTrendsResponse = (response: any, selectedWidgetClone: any, dispatch: any, t: any): void => {
    if (response?.data?.data) {
        downloadFileFromLink(response?.data?.data, selectedWidgetClone?.widgetType?.id);
        dispatch(setMessageContent({ isOpen: true, message: t('COMMON:FILE_DOWNLOAD_SUCCESS') }));
        return;
    }
    dispatch(setMessageContent({ isOpen: true, message: t('COMMON:NO_DATA') }));
};

export const dispatchWidgetFilterRefetch = (
    dispatch: any,
    endpoint: string,
    payloadData: any,
    widgetToUpdate: any,
    id: any,
    eventValue: any,
    handleError: (w: any, i: any) => void
): void => {
    dispatch((api as any)?.endpoints?.[endpoint]?.initiate(payloadData, { forceRefetch: true }))
        .then((response: any) => {
            if (response?.isError) {
                handleError(widgetToUpdate, id);
                return;
            }
            dispatch(
                setWidgetList({
                    data: {
                        ...widgetToUpdate,
                        config: buildFilterUpdatedConfig(widgetToUpdate, response?.data?.data, eventValue),
                    },
                    widgetId: id,
                })
            );
        })
        .catch((error: any) => {
            console.error('error: ', error);
            handleError(widgetToUpdate, id);
        });
};

export const buildCsvDownloadPayload = (payload: any, config: any, widgetTypeId: any, from: any, to: any): any => ({
    ...payload[widgetTypeId]({ ...config }),
    startTime: from,
    endTime: to,
    timeFilter: config?.selectedTimePeriod?.toUpperCase(),
    unit: config?.unitType,
    download: true,
    email: [],
    fileType: 'CSV',
    sendEmail: false,
});

export const dispatchCsvDownload = (
    dispatch: any,
    endpoint: string,
    payloadData: any,
    onSuccess: (r: any) => void
): void => {
    dispatch((api as any)?.endpoints?.[endpoint]?.initiate(payloadData))
        .then((response: any) => onSuccess(response))
        .catch((error: any) => {
            console.error('error: ', error);
        });
};

export const handleTrendTopicMessage = (message: any, trendsRef: any): void => {
    trendsRef?.current?.addPoints({
        timestamp: message?.timestamp,
        channelId: message?.key,
        deviceId: message?.deviceId,
        unit: message?.unit,
        value: +message?.value,
    });
};

export const buildCreateDashboardButtonSx = (theme: any): any => {
    const primary = theme?.palette?.primary;
    return {
        border: `1px solid ${primary?.main}`,
        '&:hover': {
            backgroundColor: primary?.[50],
        },
    };
};

export const resolveMaxCountForWidget = (widgets: any[] | undefined, widgetId: string): any =>
    findWidgetById(widgets, widgetId)?.config?.maxCount;

export const isSummaryLoading = (isLoading: boolean, isFetching: boolean, isConfigLoading: boolean): boolean =>
    isLoading || isFetching || isConfigLoading;

export const shouldShowCreateDashboardBtn = (
    canCreateSystemDashboard: boolean,
    canCreateUserDashboard: boolean
): boolean => canCreateSystemDashboard || canCreateUserDashboard;

export const buildCommonWidgetConfigArgs = (deviceId: any): any => ({
    deviceId,
    shouldGateWidgetQueryDeviceIdByOntologyFlag: true,
});

export const resolveGroupIdOrOrgId = (group: any, currentOrg: any): any => group?.id ?? currentOrg?.id;

export const shouldSkipDashboardsQuery = (currentOrg: any, deviceId: any): boolean => !currentOrg?.id || !deviceId;

export const getSelectedNodeWrapperType = (selectedNode: any): any => selectedNode?.wrapperDeviceType;

export const buildWidgetDataProp = (widget: any): any => {
    const { config: cfg, name, widgetType, updatedAt } = widget ?? {};
    if (!cfg) {
        return { primary: [], secondary: [], secondaryChannelCount: 6, updatedAt };
    }
    return { ...cfg, widgetName: name, widgetTypeId: widgetType?.id, updatedAt };
};

export const buildGridWidgetDataProp = (widget: any): any => {
    const { config: cfg, name, widgetType, id, updatedAt } = widget ?? {};
    if (!cfg) {
        return { primary: [], secondary: [], secondaryChannelCount: 6, updatedAt };
    }
    return {
        ...cfg,
        widgetName: name,
        widgetTypeId: widgetType?.id,
        widgetTypeName: widgetType?.name,
        id,
        updatedAt,
    };
};

export const buildMapSpread = (widget: any, handleMapConfig: any): any => ({
    countKey: 'deviceCount',
    widgetData: widget?.config,
    onIdle: handleMapConfig,
    widgetId: widget?.id,
    clustering: false,
});

export const buildTrendsSpread = (
    widget: any,
    handleTrendsDurationPayload: any,
    downloadTrends: any,
    handleLegendsPayload: any
): any => ({
    payloadChangeHandler: (widgetPayload: any): void =>
        handleTrendsDurationPayload({ ...widgetPayload, widgetId: widget?.id }),
    downloadHandler: (channelIds?: any): void => downloadTrends(widget?.id, channelIds),
    handlerLegendsPayload: (legends: any): void => handleLegendsPayload({ ...legends, widgetId: widget?.id }),
});

export const buildScoreSpread = (scoreModalRef: any): any => ({
    learnMoreCallback: (): void => {
        scoreModalRef?.current?.onClick();
    },
});

export const buildCommandBarSpread = (
    widget: any,
    canEditControls: boolean,
    modalRefs: CommandBarModalRefs,
    handleCommandBarAction: any,
    setWidgetID: any,
    dispatch: any,
    t: any
): any => ({
    commandHandler: (commandBarData: any): void =>
        handleCommandBarPress(
            commandBarData,
            widget?.id,
            canEditControls,
            modalRefs,
            handleCommandBarAction,
            setWidgetID,
            dispatch,
            t
        ),
});

export const buildLoadGroupsSpread = (
    handleLoadGroupToggle: any,
    loadGroupStates: any,
    openLoadGroupsModal: any,
    isWidgetUpdateLoading: boolean
): any => ({
    onLoadGroupToggle: (loadGroup: any, newState: GroupState, loadWidgetId: string): void =>
        handleLoadGroupToggle(loadGroup, newState, loadWidgetId),
    loadGroupStates,
    onOpenLoadGroupsModal: openLoadGroupsModal,
    isWidgetUpdating: isWidgetUpdateLoading,
});

export const buildGridLoadGroupsSpread = (
    handleLoadGroupToggle: any,
    loadGroupStates: any,
    openLoadGroupsModal: any,
    isWidgetUpdateLoading: boolean,
    setIsCreateLoadGroupModalOpen: (open: boolean) => void
): any => ({
    ...buildLoadGroupsSpread(handleLoadGroupToggle, loadGroupStates, openLoadGroupsModal, isWidgetUpdateLoading),
    onCreateLoadGroup: (): void => setIsCreateLoadGroupModalOpen(true),
});

export const buildVirtualLoadsSpread = (widget: any, onClick: any): any => ({
    addVirtualLoad: (): void => onClick({ widgetId: widget?.id, widgetType: widget?.widgetType?.id }),
});

export const buildDetailsSpread = (widget: any, handleRefreshCallback: any): any => ({
    refreshCallback: (): void => {
        handleRefreshCallback(widget?.id);
    },
});

export const buildLoadsSpread = (widget: any, handleDeviceLoads: any): any => ({
    loadsHandler: (loadsData: any): void => handleDeviceLoads(loadsData, widget?.id),
});

export const buildFilterSpread = (widgets: any[] | undefined, handleWidgetFilter: any): any => ({
    widgetFilter: (event: any, id: any): void => {
        const widgetToUpdate = findWidgetById(widgets, id);
        handleWidgetFilter(event, id, widgetToUpdate);
    },
});

export const buildAggregatedTrendsSpread = (
    widgets: any[] | undefined,
    handleUnitTypeFilter: any,
    handleDownloadCSV: any
): any => ({
    unitFilter: (event: any, id: any): void => {
        const widgetToUpdate = findWidgetById(widgets, id);
        handleUnitTypeFilter(event, id, widgetToUpdate);
    },
    downloadCSVHandler: (id: any): void => {
        const widgetToUpdate = findWidgetById(widgets, id);
        handleDownloadCSV(widgetToUpdate);
    },
});

export const buildWeatherSpread = (widgets: any[] | undefined, handleRefreshWeatherData: any): any => ({
    fetchWeatherData: (id: any): void => {
        const widgetToUpdate = findWidgetById(widgets, id);
        handleRefreshWeatherData(id, widgetToUpdate);
    },
});

export const isTrendsWidget = (widgetTypeId: any): boolean =>
    widgetTypeId === 'trends' || widgetTypeId === 'load_group_trends';

export const isTimeFilterWidget = (widgetTypeId: any): boolean =>
    widgetTypeId === 'consumption_breakdown' ||
    widgetTypeId === 'metrics' ||
    widgetTypeId === 'load_trends' ||
    widgetTypeId === 'weather' ||
    widgetTypeId === 'aggregated_trends';

type PinnedWidgetHandlers = {
    handleRefreshCallback?: (id: string) => void;
    handleMapConfig: (args: any) => any;
    scoreModalRef: any;
    handleTrendsDurationPayload: (payload: any) => void;
    downloadTrends: (id: any, cid: any) => void;
    handleLegendsPayload: (payload: any) => void;
    handleCommandBarAction: (data: any, id: string) => void;
    handleLoadGroupToggle: (lg: any, ns: GroupState, id?: string) => Promise<void>;
    loadGroupStates: Record<string, GroupState>;
    openLoadGroupsModal: (args: any) => void;
    isWidgetUpdateLoading: boolean;
    canEditControls: boolean;
    modalRefs: CommandBarModalRefs;
    setWidgetID: (id: string) => void;
    dispatch: any;
    t: any;
};

type GridWidgetHandlers = PinnedWidgetHandlers & {
    trendsRef: any;
    handleWidgetHeightChange: (id: string, h: number) => void;
    onClick: (args: any) => void;
    handleRefreshCallback: (id: any) => void;
    handleDeviceLoads: (data: any, id: string) => any;
    setIsCreateLoadGroupModalOpen: (open: boolean) => void;
    handleWidgetFilter: (event: any, id: any, w: any) => void;
    handleUnitTypeFilter: (event: any, id: any, w: any) => void;
    handleDownloadCSV: (w: any) => void;
    handleRefreshWeatherData: (id: any, w: any) => void;
    widgets: any[] | undefined;
};

type PinnedWidgetItemProps = {
    widget: any;
    loadingState: Record<string, boolean>;
    renderLoader: (type: any) => JSX.Element;
    selectedNode: any;
    handlers: PinnedWidgetHandlers;
};

export const buildPinnedDynamicWidgetSpreads = (widget: any, handlers: PinnedWidgetHandlers): any => {
    const widgetTypeId = widget?.widgetType?.id;
    const spreads: any = {};
    if (isDefaultDetailsWidget(widgetTypeId) && handlers.handleRefreshCallback) {
        Object.assign(spreads, buildDetailsSpread(widget, handlers.handleRefreshCallback));
    }
    if (widgetTypeId === 'map') Object.assign(spreads, buildMapSpread(widget, handlers.handleMapConfig));
    if (widgetTypeId === 'score') Object.assign(spreads, buildScoreSpread(handlers.scoreModalRef));
    if (isTrendsWidget(widgetTypeId)) {
        Object.assign(
            spreads,
            buildTrendsSpread(
                widget,
                handlers.handleTrendsDurationPayload,
                handlers.downloadTrends,
                handlers.handleLegendsPayload
            )
        );
    }
    if (widgetTypeId === 'command_bar') {
        Object.assign(
            spreads,
            buildCommandBarSpread(
                widget,
                handlers.canEditControls,
                handlers.modalRefs,
                handlers.handleCommandBarAction,
                handlers.setWidgetID,
                handlers.dispatch,
                handlers.t
            )
        );
    }
    if (widgetTypeId === 'load_groups') {
        Object.assign(
            spreads,
            buildLoadGroupsSpread(
                handlers.handleLoadGroupToggle,
                handlers.loadGroupStates,
                handlers.openLoadGroupsModal,
                handlers.isWidgetUpdateLoading
            )
        );
    }
    return spreads;
};

const PinnedDynamicWidget = ({
    widget,
    selectedNode,
    handlers,
}: {
    widget: any;
    selectedNode: any;
    handlers: PinnedWidgetHandlers;
}): JSX.Element => (
    <DynamicWidget
        mode="pinned-view"
        widgetTypeId={widget?.widgetType?.id}
        dropType={selectedNode?.wrapperDeviceType}
        {...buildPinnedDynamicWidgetSpreads(widget, handlers)}
        widgetData={{
            internalCdnJsLink: widget?.widgetType?.internalCdnJsLink,
            ...buildWidgetDataProp(widget),
        }}
    />
);

const PinnedWidgetItem = ({
    widget,
    loadingState,
    renderLoader,
    selectedNode,
    handlers,
}: PinnedWidgetItemProps): JSX.Element => {
    const widgetId = widget?.id;
    const widgetTypeId = widget?.widgetType?.id;
    if (!loadingState[widgetId]) {
        return (
            <Card sx={{ height: '100%' }}>
                <CardContent className="padding-0 custom-card-height">{renderLoader(widgetTypeId)}</CardContent>
            </Card>
        );
    }
    return <PinnedDynamicWidget widget={widget} selectedNode={selectedNode} handlers={handlers} />;
};

type GridDynamicWidgetProps = {
    widget: any;
    selectedNode: any;
    handlers: GridWidgetHandlers;
};

export const buildGridSharedSpreads = (widget: any, handlers: GridWidgetHandlers, widgetTypeId: any): any => {
    const spreads: any = {};
    if (widgetTypeId === 'map') Object.assign(spreads, buildMapSpread(widget, handlers.handleMapConfig));
    if (widgetTypeId === 'score') Object.assign(spreads, buildScoreSpread(handlers.scoreModalRef));
    if (isTrendsWidget(widgetTypeId)) {
        Object.assign(
            spreads,
            buildTrendsSpread(
                widget,
                handlers.handleTrendsDurationPayload,
                handlers.downloadTrends,
                handlers.handleLegendsPayload
            )
        );
    }
    if (widgetTypeId === 'command_bar') {
        Object.assign(
            spreads,
            buildCommandBarSpread(
                widget,
                handlers.canEditControls,
                handlers.modalRefs,
                handlers.handleCommandBarAction,
                handlers.setWidgetID,
                handlers.dispatch,
                handlers.t
            )
        );
    }
    return spreads;
};

export const buildGridExtraSpreads = (widget: any, handlers: GridWidgetHandlers, widgetTypeId: any): any => {
    const spreads: any = {};
    if (widgetTypeId === 'virtual_loads') {
        Object.assign(spreads, buildVirtualLoadsSpread(widget, handlers.onClick));
    }
    if (widgetTypeId === 'details') {
        Object.assign(spreads, buildDetailsSpread(widget, handlers.handleRefreshCallback));
    }
    if (widgetTypeId === 'loads') Object.assign(spreads, buildLoadsSpread(widget, handlers.handleDeviceLoads));
    if (widgetTypeId === 'load_groups') {
        Object.assign(
            spreads,
            buildGridLoadGroupsSpread(
                handlers.handleLoadGroupToggle,
                handlers.loadGroupStates,
                handlers.openLoadGroupsModal,
                handlers.isWidgetUpdateLoading,
                handlers.setIsCreateLoadGroupModalOpen
            )
        );
    }
    if (isTimeFilterWidget(widgetTypeId)) {
        Object.assign(spreads, buildFilterSpread(handlers.widgets, handlers.handleWidgetFilter));
    }
    if (widgetTypeId === 'aggregated_trends') {
        Object.assign(
            spreads,
            buildAggregatedTrendsSpread(handlers.widgets, handlers.handleUnitTypeFilter, handlers.handleDownloadCSV)
        );
    }
    if (widgetTypeId === 'weather') {
        Object.assign(spreads, buildWeatherSpread(handlers.widgets, handlers.handleRefreshWeatherData));
    }
    return spreads;
};

export const buildGridDynamicWidgetSpreads = (widget: any, handlers: GridWidgetHandlers): any => {
    const widgetTypeId = widget?.widgetType?.id;
    return {
        ...buildGridSharedSpreads(widget, handlers, widgetTypeId),
        ...buildGridExtraSpreads(widget, handlers, widgetTypeId),
    };
};

const GridDynamicWidget = ({ widget, selectedNode, handlers }: GridDynamicWidgetProps): JSX.Element => {
    const widgetId = widget?.id;
    return (
        <DynamicWidget
            mode="view"
            widgetRef={handlers.trendsRef}
            widgetTypeId={widget?.widgetType?.id}
            onHeightChange={(h: number): void => handlers.handleWidgetHeightChange(widgetId, h)}
            dropType={selectedNode?.wrapperDeviceType}
            {...buildGridDynamicWidgetSpreads(widget, handlers)}
            widgetData={{
                internalCdnJsLink: widget?.widgetType?.internalCdnJsLink,
                ...buildGridWidgetDataProp(widget),
            }}
        />
    );
};

type WidgetInfoCardProps = {
    widget: any;
    className?: string;
    descriptionKey: string;
    t: (key: string) => string;
};

const WidgetInfoCard = ({ widget, className, descriptionKey, t }: WidgetInfoCardProps): JSX.Element => (
    <Card className={className ?? 'margin-left-0'} sx={{ height: '100%' }}>
        <CardHeader
            title={
                <Typography variant="subtitle2" color="primary" fontWeight={'600'} fontSize={'14px'} padding={'16px'}>
                    {widget?.name ?? '-'}
                </Typography>
            }
            className="border-bottom-1 padding-0"
        />
        <Divider />
        <CardContent>
            <CustomEmptyState
                icon={<ErrorOutline className="height-100 width-100" />}
                title={`${widget?.name} ${t('COMMON:NOT_CONFIGURED')}`}
                className="padding-y-80"
                description={<CustomTransComponent translationKey={descriptionKey} />}
            />
        </CardContent>
    </Card>
);

type GridWidgetItemProps = {
    widget: any;
    loadingState: Record<string, boolean>;
    renderLoader: (type: any) => JSX.Element;
    selectedNode: any;
    handlers: GridWidgetHandlers;
};

const GridWidgetItem = ({
    widget,
    loadingState,
    renderLoader,
    selectedNode,
    handlers,
}: GridWidgetItemProps): JSX.Element => {
    const widgetId = widget?.id;
    if (!loadingState[widgetId]) {
        return (
            <Card sx={{ height: '100%' }}>
                <CardContent className="padding-0 custom-card-height">
                    {renderLoader(widget?.widgetType?.id)}
                </CardContent>
            </Card>
        );
    }
    const listType = resolveGatewayDeviceListType(widget, selectedNode?.wrapperDeviceType);
    if (!isWidgetApplicable(widget, listType)) {
        return (
            <WidgetInfoCard widget={widget} descriptionKey="VIRTUAL_LOAD:WIDGET_TYPE_NOT_SUPPORTED" t={handlers.t} />
        );
    }
    if (!isWidgetConfigured(widget)) {
        return (
            <WidgetInfoCard
                widget={widget}
                descriptionKey="VIRTUAL_LOAD:THIS_WIDGET_TYPE_NOT_CONFIGURED"
                t={handlers.t}
            />
        );
    }
    return <GridDynamicWidget widget={widget} selectedNode={selectedNode} handlers={handlers} />;
};

export const Summary = (): JSX.Element => {
    const theme: any = useTheme();
    const { t } = useTranslation();
    const modalRef = React.useRef<any>(null);
    const trendsRef = React.useRef<any>(null);
    const pollingRefs = React.useRef<{ [key: string]: any }>({});
    const currentOrg = useTypedSelector(selectedOrg);
    const { deviceId } = useParams();
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canCreateSystemDashboard = hasPermission('dashboard-management_create-system-dashboards');
    const canCreateUserDashboard = hasPermission('dashboard-management_create-user-dashboards');
    const widgets = useTypedSelector(widgetList);
    const loadingState = useTypedSelector((rootState) => rootState.commonDashboard.loaders);
    const selectedNode = useTypedSelector((rootState) => rootState?.common?.selectedNode);
    const canEditControls = hasPermission('device-controls_edit-devices');

    // FIJI-70205: Don't show command bar widgets to users (viewers) who don't have edit permissions
    const visibleWidgets = React.useMemo(
        () => widgets?.filter((item: any) => item?.widgetType?.id !== 'command_bar' || canEditControls) ?? [],
        [widgets, canEditControls]
    );

    const hasSingleDeviceEsmWidget = React.useMemo(
        () =>
            visibleWidgets.some(
                (item: { widgetType?: { id?: string }; config?: { multiDevice?: boolean } }) =>
                    item?.widgetType?.id === 'environmental_stress_monitoring' && !item?.config?.multiDevice
            ),
        [visibleWidgets]
    );

    const pinnedWidgetsArr = React.useMemo(
        () => visibleWidgets?.filter((item: any) => item?.config?.isPinned) ?? [],
        [visibleWidgets]
    );

    const getMinRowsForWidget = React.useCallback(
        (widgetId: string): number => computeMinRowsForWidget(findWidgetById(widgets, widgetId)),
        [widgets]
    );

    const mqttConnectionStatus = useTypedSelector((state) => state['common']['mqttConnectionStatus']);

    const topics = useTypedSelector(mqttTopics);
    const scoreModalRef = React.useRef<any>(null);
    const detailsModalRef: any = React.useRef(null);
    const detailsModalWithInputRef: any = React.useRef(null);
    const detailsModalFaultsRef: any = React.useRef(null);
    const [widgetID, setWidgetID] = React.useState<string>('');
    const [loadGroupsModalState, setLoadGroupsModalState] = React.useState<{
        isVisible: boolean;
        widgetId: string;
        isMultiDevice: boolean;
        initialSelectedIds: string[];
    }>({
        isVisible: false,
        widgetId: '',
        isMultiDevice: false,
        initialSelectedIds: [],
    });
    const [isCreateLoadGroupModalOpen, setIsCreateLoadGroupModalOpen] = React.useState(false);
    const [loadGroupStates, setLoadGroupStates] = React.useState<Record<string, GroupState>>({});
    // Actual rendered heights from DefaultComponent — used to shrink grid cells to content height
    const [measuredHeights, setMeasuredHeights] = React.useState<Record<string, number>>({});
    const handleWidgetHeightChange = React.useCallback(
        (widgetId: string, height: number) => {
            // react-grid-layout renders h rows as: h * rowHeight + (h-1) * marginY pixels.
            // Inverting: h = (height + marginY) / (rowHeight + marginY)
            const minRows = getMinRowsForWidget(widgetId);
            const widget = findWidgetById(widgets, widgetId);
            const adjustedHeight = computeAdjustedHeight(widget, height, minRows);
            const neededH = Math.max(
                minRows,
                Math.ceil((adjustedHeight + GRID_ROW_MARGIN_Y) / (GRID_ROWS + GRID_ROW_MARGIN_Y))
            );
            setMeasuredHeights((prev) => (prev[widgetId] === neededH ? prev : { ...prev, [widgetId]: neededH }));
        },
        [getMinRowsForWidget]
    );
    useNewCustomTranslations('WIDGETS');
    const loadGroupFallbackTimeoutRefs = React.useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
    const lastLoadGroupMqttAtRef = React.useRef<Record<string, number>>({});

    const markLoadGroupPending = React.useCallback((loadGroupId: number | string): void => {
        setLoadGroupStates((prev) => ({ ...prev, [String(loadGroupId)]: 'PENDING' }));
    }, []);

    const getLoadGroupStateFromMqttMessage = React.useCallback(
        (message: any): GroupState | null => decodeMqttGroupState(message),
        []
    );

    const {
        data: dashboards,
        isLoading,
        isFetching,
        isSuccess,
        isError,
    }: any = useGetActiveDashboardsQuery(
        {
            params: {
                resourceType: 'DEVICE',
                resourceTypeId: deviceId,
            },
        },
        {
            skip: shouldSkipDashboardsQuery(currentOrg, deviceId),
        }
    );

    const group = useTypedSelector((state) => state.common.selectedNode);
    const dispatch = useAppDispatch();

    const {
        onClick,
        data: virtualLoadWidgetData,
        onConfirm,
        isVisible,
        onCancel,
    } = useConfirm((args: any): void => handleSubmit(args) as any);
    useNewCustomTranslations('WIDGETS');

    const { isSuccess: createIsSuccess, handleSubmit } = useHandleVirtualLoads(virtualLoadWidgetData);
    const [updateLoadGroups] = useUpdateLoadGroupsMutation();
    const [updateWidget, { isLoading: isWidgetUpdateLoading }] = useUpdateWidgetMutation();
    const [getWidgetConfig] = useLazyGetDashboardWidgetByIdQuery();
    const [getChannelDetails] = useGetChannelDetailsMutation();

    const { isLoading: isConfigLoading } = useCommonWidgetConfiguration(
        dashboards?.data,
        buildCommonWidgetConfigArgs(deviceId)
    );

    const { data: profileDetails } = useGetUserProfileQuery({}) as ApiEndpointType<ApiResponseType<UserProfile>>;

    const { payload, response: widgetResponse } = useGetWidgetDetails({
        selectorType: 'commonDashboard',
        dataGetter: {
            groupId: resolveGroupIdOrOrgId(group, currentOrg),
            deviceId: deviceId,
        },
    });

    const { handleDeviceCommands, handleDeviceLoads } = useDashboardCommonHandlers(deviceId);

    const refreshLoadGroupsWidget = React.useCallback(
        async (widgetId: string): Promise<void> => {
            const selectedWidget = findWidgetById(widgets, widgetId);
            if (!selectedWidget?.config) return;
            await refetchLoadGroupsList(dispatch, payload, selectedWidget.config, widgetId, widgetResponse);
        },
        [dispatch, payload, widgetResponse, widgets]
    );

    const handleLoadGroupToggle = async (loadGroup: any, newState: GroupState, widgetId?: string): Promise<void> => {
        if (!loadGroup?.id) return;

        const isEnabled = newState === 'ENABLED';
        const { error }: any = await updateLoadGroups({
            loadGroupId: loadGroup.id,
            body: {
                performGroupSwitchAction: true,
                isGroupEnabledOrDisabled: isEnabled,
            },
        });

        if (error) {
            throw error;
        }
        dispatch(
            setMessageContent({
                isOpen: true,
                message: `${loadGroup?.name} ${isEnabled ? 'turn on' : 'turn off'} initiated`,
                duration: 3000,
            })
        );

        markLoadGroupPending(loadGroup.id);

        if (!widgetId) return;
        const timeoutKey = `${widgetId}:${String(loadGroup.id)}`;
        if (loadGroupFallbackTimeoutRefs.current[timeoutKey]) {
            clearTimeout(loadGroupFallbackTimeoutRefs.current[timeoutKey]!);
        }
        const toggleStartedAt = Date.now();
        loadGroupFallbackTimeoutRefs.current[timeoutKey] = scheduleLoadGroupFallback(
            loadGroup.id,
            widgetId,
            toggleStartedAt,
            lastLoadGroupMqttAtRef,
            refreshLoadGroupsWidget,
            setLoadGroupStates
        );
    };

    const handleLoadGroupsSelect = async (selectedGroups: any[], widgetId: string): Promise<void> => {
        const selectedWidget = findWidgetById(widgets, widgetId);
        if (!selectedWidget) return;

        const configClone = cloneConfig(selectedWidget?.config);

        configClone['selectedNodes'] = buildLoadGroupsSelectedNodes(selectedGroups);
        delete configClone.secondary;

        const { error }: any = await updateWidget({
            widgetId,
            body: {
                config: configClone,
                sourceId: selectedWidget?.sourceId ?? null,
                sourceName: selectedWidget?.sourceName ?? null,
            },
        });

        if (error) {
            throw error;
        }

        await refetchLoadGroupsList(dispatch, payload, configClone, widgetId, widgetResponse);

        dispatch(
            setMessageContent({
                isOpen: true,
                message: `${selectedGroups?.length} ${t('WIDGETS:LOADS_GROUPS.LOADS_HAVE_BEEN_ADDED_SUCCESSFULLY')}`,
                duration: 3000,
            })
        );
    };

    const openLoadGroupsModal = (args: {
        widgetId: string;
        isMultiDevice: boolean;
        initialSelectedIds: string[];
    }): void => {
        setLoadGroupsModalState({
            isVisible: true,
            widgetId: args.widgetId,
            isMultiDevice: args.isMultiDevice,
            initialSelectedIds: args.initialSelectedIds,
        });
    };

    const closeLoadGroupsModal = (): void => {
        setLoadGroupsModalState((prev) => ({ ...prev, isVisible: false }));
    };

    // Always keep a fresh reference to widgets for use in callbacks
    const widgetsRef = React.useRef(widgets);
    React.useEffect(() => {
        widgetsRef.current = widgets;
    }, [widgets]);

    // Track pending command poll timeouts so they can be cancelled on unmount
    // or before scheduling a new poll for the same widget.
    const commandPollTimeoutRefs = React.useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
    const lastCommandMqttAtByWidgetRef = React.useRef<Record<string, number>>({});
    const lastAvailableCommandsFetchAtByWidgetRef = React.useRef<Record<string, number>>({});

    // After sendDeviceCommand HTTP settles, keep the spinner alive while we poll for
    // the updated available controls, then update button names once the response arrives.
    // This is the HTTP fallback when MQTT is not available in the browser.
    const handleCommandBarAction = React.useCallback(
        (commandBarData: any, widgetId: string): void => {
            void handleDeviceCommands(commandBarData, widgetId).then((result) => {
                if (!result?.isSuccess) return;

                // Re-apply isLoading on the clicked channel in the NESTED secondary so
                // the spinner persists while getAvailableCommands is in-flight.
                // useDashboardCommonHandlers already restored the nested preCommandConfig;
                // we just need to mark the one channel that's still processing.
                const loadingConfig = cloneConfig(widgetsRef.current?.find((w: any) => w?.id === widgetId)?.config);
                applyLoadingToChannel(loadingConfig, commandBarData);
                dispatch(configureSummaryValues({ widgetId, config: loadingConfig }));

                const existingTimeout = commandPollTimeoutRefs.current[widgetId];
                if (existingTimeout) {
                    clearTimeout(existingTimeout);
                }
                const commandStartedAt = Date.now();
                const fallbackDelayMs = mqttConnectionStatus ? 30000 : 10000; //Reduced time for showing loader
                commandPollTimeoutRefs.current[widgetId] = setTimeout(() => {
                    const lastMqttAt = lastCommandMqttAtByWidgetRef.current[widgetId] ?? 0;
                    // If MQTT delivered the command refresh, don't double-fetch (even if MQTT connected late).
                    if (lastMqttAt >= commandStartedAt) return;

                    const freshWidget = widgetsRef.current?.find((w: any) => w?.id === widgetId);
                    if (!freshWidget?.config) return;
                    dispatch(
                        (api as any)?.endpoints?.['getAvailableCommands']?.initiate(
                            payload['command_bar-getAvailableCommands'](
                                freshWidget.config,
                                freshWidget?.selectedEntities
                            ),
                            { forceRefetch: true }
                        )
                    ).then((response: any) => {
                        if (!response?.error) {
                            widgetResponse['command_bar']([response], widgetId);
                            return;
                        }
                        const fallback = buildLoadingClearedFallback(
                            widgetsRef.current?.find((w: any) => w?.id === widgetId)
                        );
                        dispatch(configureSummaryValues({ widgetId, config: fallback }));
                    });
                }, fallbackDelayMs);
            });
        },
        [handleDeviceCommands, dispatch, payload, widgetResponse, mqttConnectionStatus]
    );

    // Ref to track last map config call and prevent duplicates
    const lastMapConfigRef = React.useRef<string | null>(null);

    const handleMapConfig = React.useCallback(
        (args: any): any => {
            // Create a key from the args to detect duplicates
            const argsKey = JSON.stringify(args);

            // Skip if same as last call
            if (lastMapConfigRef.current === argsKey) {
                return;
            }
            lastMapConfigRef.current = argsKey;

            const selectedWidgetClone = cloneConfig(findWidgetById(widgets, args.widgetId));

            dispatch(
                (api as any)?.endpoints?.['deviceMap']?.initiate({
                    ...args?.bounds,
                    filters: buildMapConfigFilters(selectedWidgetClone, selectedNode, deviceId),
                })
            )?.then(({ data }: any) => {
                if (!data) return;
                selectedWidgetClone['config']['secondary'] = mapDeviceMapItemsToSecondary(data?.data);
                dispatch(configureSummaryValues({ widgetId: args?.widgetId, config: selectedWidgetClone.config }));
            });
        },
        [widgets, dispatch, getSelectedNodeWrapperType(selectedNode), deviceId]
    );

    const startEnergyFlowPolling = (widget: any): void => {
        if (pollingRefs.current[`${widget.id}${deviceId}`]) {
            clearInterval(pollingRefs.current[`${widget.id}${deviceId}`]);
        }

        pollingRefs.current[`${widget.id}${deviceId}`] = setInterval(() => {
            const widgetTypeId = widget?.widgetType?.id;
            const selectedWidgetConfig = findWidgetById(widgets, widget?.id)?.config;
            if (widgetTypeId === 'energy_flow') {
                dispatchWidgetPolling(
                    dispatch,
                    payload,
                    'energy_flow',
                    'getEnergyFLowAssetsData',
                    selectedWidgetConfig,
                    widget
                );
            }
            if (widgetTypeId === 'metrics') {
                dispatchWidgetPolling(
                    dispatch,
                    payload,
                    'metrics',
                    'getMetricsWidgetData',
                    selectedWidgetConfig,
                    widget
                );
            }
        }, ENERGY_FLOW_WIDGET_POLLING_INTERVAL_IN_MS);
    };

    const downloadTrends = (widgetId: any, selectedPayload: any): void => {
        const widget = findWidgetById(widgets, widgetId);
        const selectedWidgetClone = cloneConfig(widget?.config);

        const widgetType = widget?.widgetType?.id;
        const payloadKey = getTrendsPayloadKey(widgetType);

        if (!selectedWidgetClone) return;
        const exportBody = buildTrendsExportBody(
            payloadKey,
            selectedPayload,
            payload,
            selectedWidgetClone,
            widget?.selectedEntities
        );
        const requestBody = buildExportTrendsRequestBody(exportBody, selectedWidgetClone, payloadKey);
        dispatch((api as any)?.endpoints?.['exportTrends']?.initiate(requestBody)).then((response: any) =>
            handleExportTrendsResponse(response, selectedWidgetClone, dispatch, t)
        );
    };

    const handleTrendsDurationPayload = (trendsPayload: any): void => {
        const widget = findWidgetById(widgets, trendsPayload?.widgetId);
        const selectedWidgetClone = cloneConfig(widget?.config);
        const widgetType = widget?.widgetType?.id;
        const payloadKey = getTrendsPayloadKey(widgetType);
        if (!selectedWidgetClone) return;
        const newPayload = buildDurationTrendsPayload(
            widgetType,
            payloadKey,
            trendsPayload,
            payload,
            selectedWidgetClone,
            widget?.selectedEntities
        );
        dispatch(removeSummaryWidgetLoader(trendsPayload?.widgetId));
        dispatchTrendsRefetch(
            dispatch,
            getTrendsApiEndpoint(widgetType),
            newPayload,
            trendsPayload,
            widgetType,
            widgetResponse
        );
    };

    const refreshDetailsWidget = async (widgetId: string): Promise<void> => {
        const result = await getWidgetConfig({ widgetId, deviceId });
        if (result.error || !result.data) return;

        const { data: refreshedWidget } = result.data as ApiResponseType<{
            id: string;
            config: Record<string, unknown>;
            selectedEntities?: unknown;
        }>;
        const existingWidget = findWidgetById(widgetsRef.current, widgetId);
        if (!refreshedWidget || !existingWidget) return;

        const selectedWidget = { ...existingWidget, ...refreshedWidget };
        dispatch(setWidgetList({ data: selectedWidget, widgetId }));
        const newPayload = payload['details']({
            config: cloneConfig(selectedWidget.config),
            selectedEntities: selectedWidget.selectedEntities,
        });

        dispatch(removeSummaryWidgetLoader(widgetId));
        const response = await getChannelDetails(newPayload);
        dispatch(setSummaryResponse({ widget: selectedWidget, responses: [response] }));
    };

    const handleRefreshCallback = (widgetId: string): void => {
        void refreshDetailsWidget(widgetId);
    };

    const getLegendsPayloadKey = (widgetType: string): string => getTrendsPayloadKey(widgetType);

    const getLegendsApiEndpoint = (widgetType: string): string => getTrendsApiEndpoint(widgetType);

    const handleLegendsPayload = (selectionPayload: any): void => {
        const widget = findWidgetById(widgets, selectionPayload?.widgetId);
        const selectedWidgetClone = JSON.parse(JSON.stringify(widget ?? {}));
        const config = selectedWidgetClone?.config;
        if (!config) return;
        const widgetType = widget?.widgetType?.id;
        const payloadKey = getLegendsPayloadKey(widgetType);
        if (payloadKey === 'trends') {
            updateTrendsConfigSecondary(selectedWidgetClone, selectionPayload, dispatch);
        }
        const newPayload = buildLegendsRequestPayload(
            widgetType,
            payloadKey,
            selectionPayload,
            payload,
            selectedWidgetClone,
            widget?.selectedEntities
        );
        dispatchLegendsRefetch(
            dispatch,
            getLegendsApiEndpoint(widgetType),
            newPayload,
            selectedWidgetClone,
            selectionPayload,
            widgetResponse
        );
    };

    const removingLoadingKey = (widgetClone: any): any => stripLoadingKeysFromWidget(widgetClone);

    const handleLoadGroupStateChange = (message: any): void => {
        if (message?.loadGroupId) {
            lastLoadGroupMqttAtRef.current[String(message.loadGroupId)] = Date.now();
        }
        const mqttState = getLoadGroupStateFromMqttMessage(message);
        setLoadGroupStates((prev) => {
            const next = { ...prev };
            if (next[message.loadGroupId] === 'PENDING') {
                dispatch(
                    setMessageContent({
                        isOpen: true,
                        message: `${message?.loadGroupName} ${
                            mqttState === 'ENABLED' ? 'turn on' : 'turn off'
                        } completed`,
                    })
                );
            }
            if (message?.loadGroupId && mqttState) {
                next[message.loadGroupId] = mqttState;
            }
            return next;
        });
    };

    const isCommandBarWidgetForDevice = (item: any, selectedDeviceId: string): boolean =>
        isCommandBarWidgetForDeviceItem(item, selectedDeviceId);

    const processCommandWidget = (widget: any): void => {
        const widgetId = widget?.id;
        lastCommandMqttAtByWidgetRef.current[widgetId] = Date.now();
        const lastFetchAt = lastAvailableCommandsFetchAtByWidgetRef.current[widgetId] ?? 0;
        if (isCommandFallbackTooSoon(lastFetchAt)) return;
        lastAvailableCommandsFetchAtByWidgetRef.current[widgetId] = Date.now();
        const widgetClone = JSON.parse(JSON.stringify(widget?.config));
        removingLoadingKey(widgetClone);
        // Immediately clear any stuck isLoading from secondary before API response
        dispatch(configureSummaryValues({ widgetId, config: widgetClone }));
        dispatch(
            (api as any)?.endpoints?.['getAvailableCommands']?.initiate(
                payload['command_bar-getAvailableCommands'](widgetClone, widget?.selectedEntities),
                { forceRefetch: true }
            )
        ).then((response: any) => {
            widgetResponse['command_bar']([response], widgetId);
        });
    };

    const handleCommandTopicMessage = (message: any, messageTopic: string, splitedArr: any): void => {
        if (isFailedLoadTriggerMessage(message) && topics?.includes(messageTopic)) {
            dispatch(setFailedLoadTrigger(message));
        }
        if (isLoadGroupStateChangeMessage(message)) {
            handleLoadGroupStateChange(message);
        }
        if (!isCommandOrRunTestMessage(message)) return;

        const selectedDeviceId = splitedArr[splitedArr?.length - 1];
        const widgetsClone = JSON.parse(JSON.stringify(widgets));
        const filteredWidgets = widgetsClone?.filter((item: any) =>
            isCommandBarWidgetForDevice(item, selectedDeviceId)
        );
        dispatchCommandMessageAlert(message, dispatch);
        filteredWidgets?.forEach((widget: any) => processCommandWidget(widget));
    };

    const mqttResponseHandler = (message: any, messageTopic: string): void => {
        const splitedArr: any = messageTopic.split('/');
        const requiredTopic = splitedArr[0]?.concat(`/${splitedArr[1]}`);
        if (requiredTopic === 'BSSRM/TREND') {
            handleTrendTopicMessage(message, trendsRef);
            return;
        }
        if (requiredTopic === 'BSSRM/COMMAND_TOPIC') {
            handleCommandTopicMessage(message, messageTopic, splitedArr);
        }
    };
    React.useEffect(() => {
        if (widgets?.every((item: any) => loadingState[item?.id])) {
            const filteredWidgets = widgets?.filter(
                (item: any) => item?.widgetType?.id === 'energy_flow' || item?.widgetType?.id === 'metrics'
            );
            if (filteredWidgets?.length) {
                filteredWidgets?.forEach((widget: any) => {
                    startEnergyFlowPolling(widget);
                });
            }
        }
    }, [widgets, loadingState]);

    React.useEffect(() => {
        const readyForWidgets = Object.keys(loadingState)?.length === widgets?.length;
        if (mqttConnectionStatus === true && readyForWidgets) {
            subscribeTopic(topics, mqttResponseHandler);
        }

        return (): void => {
            if (topics?.length) {
                unsubscribeTopic(topics);
            }
        };
    }, [mqttConnectionStatus, topics, loadingState]);

    const handleWidgetFilter = (event: any, id: any, widgetToUpdate: any): void => {
        const widgetTypeId = widgetToUpdate?.widgetType?.id;
        const eventValue = event?.target?.value;
        const { from, to, prevFrom, prevTo } = resolveWidgetTimeWindow(widgetToUpdate, profileDetails, eventValue);
        const { config } = widgetToUpdate;
        const data = payload[widgetTypeId]({ ...config });
        const normalizedTimeFilter = eventValue?.toUpperCase();

        const payloadData = buildTimeWindowedPayloadData(
            data,
            from,
            to,
            prevFrom,
            prevTo,
            normalizedTimeFilter,
            widgetTypeId,
            config?.unitType
        );

        const endpoint: any = resolveWidgetEndpoint(widgetTypeId, config?.type);
        if (!endpoint) {
            console.error('Invalid endpoint');
            return;
        }

        dispatchWidgetFilterRefetch(dispatch, endpoint, payloadData, widgetToUpdate, id, eventValue, handleError);
    };

    const handleError = (widgetToUpdate: any, id: any): void => {
        dispatch(
            setWidgetList({
                data: {
                    ...widgetToUpdate,
                    config: {
                        ...widgetToUpdate.config,
                        secondary: {},
                    },
                },
                widgetId: id,
            })
        );
    };

    const buildUnitTypePayloadData = (widgetToUpdate: any, config: any, eventValue: any): any => {
        const widgetTypeId = widgetToUpdate?.widgetType?.id;
        const { from, to } = resolveConfigTimeWindow(config, profileDetails);
        return {
            ...payload[widgetTypeId]({ ...config }),
            startTime: from,
            endTime: to,
            timeFilter: config?.selectedTimePeriod?.toUpperCase(),
            unit: eventValue,
        };
    };

    const handleUnitTypeResponse = (response: any, widgetToUpdate: any, id: any, eventValue: any): void => {
        const secondary = response?.isError ? {} : response?.data?.data;
        const extraConfig = response?.isError ? {} : { unitType: eventValue };
        dispatch(
            setWidgetList({
                data: { ...widgetToUpdate, config: { ...widgetToUpdate.config, secondary, ...extraConfig } },
                widgetId: id,
            })
        );
    };

    const handleUnitTypeFilter = (event: any, id: any, widgetToUpdate: any): void => {
        const { config } = widgetToUpdate;
        const eventValue = event?.target?.value;
        const payloadData = buildUnitTypePayloadData(widgetToUpdate, config, eventValue);
        const widgetTypeId = widgetToUpdate?.widgetType?.id;
        const endpoint = resolveWidgetEndpoint(widgetTypeId, config?.type);
        dispatch((api as any)?.endpoints?.[endpoint]?.initiate(payloadData))
            .then((response: any) => handleUnitTypeResponse(response, widgetToUpdate, id, eventValue))
            .catch((error: any) => {
                console.error('error: ', error);
            });
    };

    const handleCsvDownloadSuccess = (response: any): void => {
        const fetchUrl: any = response?.data?.data?.filter((dataUrl: any) => dataUrl?.reportType === 'Download');
        downloadFile(fetchUrl[0]?.url, 'Aggregated_Trends.csv');
        dispatch(setMessageContent({ isOpen: true, message: fetchUrl[0]?.message, duration: 3000 }));
    };

    const handleDownloadCSV = (widgetToUpdate: any): void => {
        const { config } = widgetToUpdate;
        const widgetTypeId = widgetToUpdate?.widgetType?.id;
        const { from, to } = resolveConfigTimeWindow(config, profileDetails);
        const endpoint = resolveWidgetEndpoint(widgetTypeId, config?.downloadType);
        if (!endpoint) return;
        const payloadData = buildCsvDownloadPayload(payload, config, widgetTypeId, from, to);
        dispatchCsvDownload(dispatch, endpoint, payloadData, handleCsvDownloadSuccess);
    };

    const handleRefreshWeatherData = (id: any, widgetToUpdate: any): void => {
        const { config } = widgetToUpdate;
        const widgetTypeId = widgetToUpdate?.widgetType?.id;
        const endpoint = resolveWidgetEndpoint(widgetTypeId, config?.type);
        dispatch(
            (api as any)?.endpoints?.[endpoint]?.initiate(
                { ...payload[widgetTypeId]({ ...config }) },
                { forceRefetch: true }
            )
        )
            .then((response: any) => {
                const secondary = response?.isError ? {} : response?.data?.data;
                dispatch(
                    setWidgetList({
                        data: { ...widgetToUpdate, config: { ...widgetToUpdate.config, secondary } },
                        widgetId: id,
                    })
                );
            })
            .catch((error: any) => {
                console.error('error: ', error);
            });
    };

    const renderLoader = (type: any): JSX.Element => {
        if (getWidgetTypeValidationConfig(type)['loaderType'] === 'skeleton') {
            return (
                <DetailsCardSkeleton
                    secondarylength={5}
                    {...(getWidgetTypeValidationConfig(type)?.['primarySkeletonCount'] && {
                        primarylength: getWidgetTypeValidationConfig(type)?.['primarySkeletonCount'],
                    })}
                />
            );
        }
        return <Loader size={55} />;
    };

    React.useEffect(() => {
        dispatch(resetLocalDashboard());
        dispatch(resetTrendsLegendData());
    }, []);

    React.useEffect(() => () => dispatch(resetDashboard()), []);

    React.useEffect(
        () => () => {
            Object.values(commandPollTimeoutRefs?.current ?? {}).forEach((timeoutId) => {
                if (timeoutId) clearTimeout(timeoutId);
            });
            Object.values(loadGroupFallbackTimeoutRefs?.current ?? {}).forEach((timeoutId) => {
                if (timeoutId) clearTimeout(timeoutId);
            });
        },
        []
    );

    React.useEffect(
        () => () => {
            Object.values(pollingRefs?.current ?? {}).forEach((intervalId) => {
                clearInterval(intervalId);
            });
        },
        [deviceId]
    );

    React.useEffect(() => {
        if (dashboards) {
            dispatch(setDashboardId(dashboards?.data?.id));
        }
    }, [dashboards]);

    React.useEffect(() => {
        if (createIsSuccess) onCancel();
    }, [createIsSuccess]);

    function getWidgetLayout(): any {
        const baseLayout = buildBaseWidgetLayout(widgets, dashboards);
        if (!baseLayout) return baseLayout;

        // Apply measured heights collected from actual DefaultComponent DOM renders.
        // measuredHeights is populated via onHeightChange callbacks from DefaultComponent.
        // Allow both increase (content wraps on narrow screens) and decrease (empty gray space).
        return baseLayout.map((layoutItem: any) =>
            applyLayoutMeasuredHeight(layoutItem, measuredHeights, getMinRowsForWidget(layoutItem.i))
        );
    }

    return (
        <>
            <SnoozeHeader />
            {hasSingleDeviceEsmWidget && <EsmCriticalHeader deviceId={deviceId} organizationId={currentOrg?.id} />}

            {pinnedWidgetsArr.map((widget: any) => {
                const isPinnedCommandBar = widget?.widgetType?.id === 'command_bar';

                return (
                    <Box key={widget?.id} sx={isPinnedCommandBar ? { mx: '16px', pr: '10px' } : undefined}>
                        <PinnedWidgetItem
                            widget={widget}
                            loadingState={loadingState}
                            renderLoader={renderLoader}
                            selectedNode={selectedNode}
                            handlers={{
                                handleRefreshCallback,
                                handleMapConfig,
                                scoreModalRef,
                                handleTrendsDurationPayload,
                                downloadTrends,
                                handleLegendsPayload,
                                handleCommandBarAction,
                                handleLoadGroupToggle,
                                loadGroupStates,
                                openLoadGroupsModal,
                                isWidgetUpdateLoading,
                                canEditControls,
                                modalRefs: {
                                    faults: detailsModalFaultsRef,
                                    withInput: detailsModalWithInputRef,
                                    single: detailsModalRef,
                                },
                                setWidgetID,
                                dispatch,
                                t,
                            }}
                        />
                    </Box>
                );
            })}
            <Stack sx={{ mx: '16px', mb: '16px', mt: '2px' }}>
                {isEmptyDashboardsReady(dashboards, isSuccess, isError, isLoading, isFetching, isConfigLoading) ? (
                    <Stack alignItems={'center'} justifyContent={'center'} sx={{ height: 'calc(100vh - 200px)' }}>
                        <CustomEmptyState
                            icon={<Widgets fontSize="inherit" />}
                            title={(<CustomTransComponent translationKey={'WIDGETS:NO_WIDGETS_FOUND'} />) as any}
                            description={(<CustomTransComponent translationKey={'WIDGETS:NO_WIDGET_DESC'} />) as any}
                            actions={
                                shouldShowCreateDashboardBtn(canCreateSystemDashboard, canCreateUserDashboard) && (
                                    <Button
                                        sx={buildCreateDashboardButtonSx(theme)}
                                        variant="outlined"
                                        color="primary"
                                        data-testid="summary-create-dashboard-btn"
                                        startIcon={<DesignServices />}
                                        onClick={(): void =>
                                            modalRef?.current?.handleModalAction?.(true, {
                                                assignType: 'DEVICE',
                                                assignTypeIds: [deviceId],
                                            })
                                        }
                                    >
                                        <CustomTransComponent translationKey={'DASHBOARDS:CREATE_DASHBOARD'} />
                                    </Button>
                                )
                            }
                        />
                    </Stack>
                ) : dashboards?.data && !visibleWidgets?.length && !isLoading && !isFetching && !isConfigLoading ? (
                    <Stack alignItems={'center'} justifyContent={'center'} sx={{ height: 'calc(100vh - 200px)' }}>
                        <CustomEmptyState
                            icon={<Widgets fontSize="inherit" />}
                            title={(<CustomTransComponent translationKey={'WIDGETS:NO_WIDGETS_FOUND'} />) as any}
                            description={(<CustomTransComponent translationKey={'WIDGETS:NO_WIDGET_DESC'} />) as any}
                        />
                    </Stack>
                ) : (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        {
                            // TODO: react-beautiful-dnd package is deprecated and we need to find an alternative package for drag and drop
                        }
                        {/* @ts-ignore */}
                        <ResponsiveReactGridLayout
                            className="layout w-100 view-dashboard"
                            isResizable={false}
                            compactType="vertical"
                            containerPadding={[WIDGET_GRID_GAP, WIDGET_GRID_GAP]}
                            margin={GRID_LAYOUT_MARGIN}
                            draggableHandle=".drag-handle"
                            layout={isDashboardAlignedWithWidgets(widgets, dashboards) && getWidgetLayout()}
                            style={{ position: 'absolute', paddingRight: '10px' }}
                            cols={GRID_COLS}
                            rowHeight={GRID_ROWS}
                        >
                            {Boolean(visibleWidgets.length) &&
                                visibleWidgets.map(
                                    (widget: any) =>
                                        !widget?.config?.isPinned && (
                                            <div key={widget?.id} style={{ zIndex: 999 }}>
                                                <GridWidgetItem
                                                    widget={widget}
                                                    loadingState={loadingState}
                                                    renderLoader={renderLoader}
                                                    selectedNode={selectedNode}
                                                    handlers={{
                                                        handleMapConfig,
                                                        scoreModalRef,
                                                        handleTrendsDurationPayload,
                                                        downloadTrends,
                                                        handleLegendsPayload,
                                                        handleCommandBarAction,
                                                        handleLoadGroupToggle,
                                                        loadGroupStates,
                                                        openLoadGroupsModal,
                                                        isWidgetUpdateLoading,
                                                        canEditControls,
                                                        modalRefs: {
                                                            faults: detailsModalFaultsRef,
                                                            withInput: detailsModalWithInputRef,
                                                            single: detailsModalRef,
                                                        },
                                                        setWidgetID,
                                                        dispatch,
                                                        t,
                                                        trendsRef,
                                                        handleWidgetHeightChange,
                                                        onClick,
                                                        handleRefreshCallback,
                                                        handleDeviceLoads,
                                                        setIsCreateLoadGroupModalOpen,
                                                        handleWidgetFilter,
                                                        handleUnitTypeFilter,
                                                        handleDownloadCSV,
                                                        handleRefreshWeatherData,
                                                        widgets,
                                                    }}
                                                />
                                            </div>
                                        )
                                )}
                        </ResponsiveReactGridLayout>
                    </Box>
                )}
            </Stack>
            {/* Create Dashboard Modal */}
            <CreateDashboardModal data-testid="summary-create-dashboard-modal" key="createsn@d3" ref={modalRef} />
            <AddVirtualModal
                data-testid="summary-add-virtual-modal"
                key="virtual@1231"
                isVisible={isVisible}
                onConfirm={onConfirm}
                onCancel={onCancel}
            />
            <SelectLoadGroupsModal
                isVisible={loadGroupsModalState.isVisible}
                isMultiDevice={loadGroupsModalState.isMultiDevice}
                deviceId={deviceId}
                initialSelectedIds={loadGroupsModalState.initialSelectedIds}
                isUpdating={isWidgetUpdateLoading}
                maxCount={resolveMaxCountForWidget(widgets, loadGroupsModalState.widgetId)}
                onCancel={closeLoadGroupsModal}
                onConfirm={(selectedGroups: any[]): void => {
                    void handleLoadGroupsSelect(selectedGroups, loadGroupsModalState.widgetId);
                    closeLoadGroupsModal();
                }}
            />
            <CreateLoadGroupModal
                isOpen={isCreateLoadGroupModalOpen}
                onClose={(): void => setIsCreateLoadGroupModalOpen(false)}
                deviceId={deviceId}
            />
            <AddConfirmationModal
                data-testid="summary-command-confirmation-modal"
                ref={detailsModalRef}
                widgetID={widgetID}
                onConfirmCommand={handleCommandBarAction}
            />
            <AddConfirmationModalWithInput
                data-testid="summary-command-confirmation-modal-with-input"
                ref={detailsModalWithInputRef}
                widgetID={widgetID}
                onConfirmCommand={handleCommandBarAction}
            />
            <AddConfirmationForFaultsWarnings
                data-testid="summary-command-confirmation-modal-for-faults-warnings"
                ref={detailsModalFaultsRef}
                widgetID={widgetID}
                onConfirmCommand={handleCommandBarAction}
            />
            <ScoreModal data-testid="summary-score-modal" key="score@12de" ref={scoreModalRef} />
            <FailedLoadTriggerModal />
            {isSummaryLoading(isLoading, isFetching, isConfigLoading) && <Loader size={60} />}
        </>
    );
};
